
import { supabase } from '@/integrations/supabase/client';
import { AuthUser } from '@/types/auth';
import { toast } from '@/components/ui/use-toast';
import { storeUser } from './userStorage';
import * as Sentry from "@sentry/react";

/**
 * Perform user signup
 */
export const performSignup = async (
  email: string, 
  password: string, 
  ageRange: string, 
  educationLevel: string, 
  country?: string
): Promise<AuthUser> => {
  // Set Sentry breadcrumb
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Signup attempt',
    level: 'info',
    data: {
      ageRange,
      educationLevel,
      country: country || 'United States',
    },
  });
  
  // Simple validation
  if (!email || !password) {
    const error = new Error('Email and password are required');
    Sentry.captureException(error);
    throw error;
  }
  
  try {
    // Call Supabase auth API
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          age_range: ageRange,
          education_level: educationLevel,
          country: country || 'United States',
        }
      }
    });
    
    if (error) {
      // Capture signup errors in Sentry
      Sentry.captureException(error, {
        tags: { auth_method: 'email_password', action: 'signup' },
        contexts: {
          auth: { 
            provider: 'supabase', 
            email,
            ageRange,
            educationLevel,
            country: country || 'United States',
          },
        },
      });
      throw new Error(error.message);
    }
    
    if (!data.user) {
      const noUserError = new Error('Signup failed: No user returned');
      Sentry.captureException(noUserError);
      throw noUserError;
    }
    
    // Create user object
    const user: AuthUser = {
      id: data.user.id,
      email: data.user.email || '',
      ageRange,
      educationLevel,
      country: country || 'United States',
      roles: ['user'], // Default role
    };
    
    // Store user in localStorage
    storeUser(user);
    
    // Set user identifier in Sentry
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.email.split('@')[0],
    });
    
    // Add user demographics to Sentry context
    Sentry.setContext("user_demographics", {
      ageRange: user.ageRange,
      educationLevel: user.educationLevel,
      country: user.country,
    });
    
    // Add signup success breadcrumb
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Signup successful',
      level: 'info',
    });
    
    toast({
      title: "Success",
      description: "Your account has been created.",
    });
    
    return user;
  } catch (error) {
    // Ensure all errors are captured
    if (!(error instanceof Error)) {
      const unknownError = new Error('Unknown signup error');
      Sentry.captureException(unknownError);
      throw unknownError;
    }
    
    // Re-throw the original error
    throw error;
  }
};
